import { useState, useEffect } from 'react';
import { Alert, AlertTitle, Grid, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import PageTabNavigation, { PageTabItem } from 'src/shared/components/navigations/pageTabNavigation';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { getEInvoiceSender } from 'src/shared/services/electronicInvoiceSenderService';
import { getAllInvoiceGroups } from 'src/shared/services/invoiceGroupService';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useEInvoiceApplicationsBadge } from '../hooks/useEInvoiceApplicationsBadge';

export const EInvoicePage = () => {
  const { t } = useTranslation();
  const { currentUser } = useGlobals();
  const isMounted = useIsMounted();
  const [hasPendingApplications, refetchApplicationsBadge] = useEInvoiceApplicationsBadge();
  const [hasAnySender, setHasAnySender] = useState<boolean | null>(null);

  useEffect(() => {
    getAllInvoiceGroups(0, 500, 'name', 'asc', '')
      .then((res) => {
        const groups = res.data.results;
        if (groups.length === 0) {
          if (isMounted()) setHasAnySender(false);
          return Promise.resolve();
        }
        return Promise.all(
          groups.map((g) =>
            getEInvoiceSender(g.owner_id)
              .then((senderRes) => !!senderRes.data)
              .catch(() => false),
          ),
        ).then((results) => {
          if (isMounted()) setHasAnySender(results.some((r) => r));
        });
      })
      .catch(() => {
        if (isMounted()) setHasAnySender(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const otherLabelPlural = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.OtherLabelPlural, t('member:otherContact.pageHeader'));

  const pageTabs: PageTabItem[] = [
    {
      label: 'invoice:eInvoice.tabReceivers',
      route: RoutesEnum.EInvoice,
    },
    {
      label: 'invoice:eInvoice.tabLinkedMembers',
      route: RoutesEnum.EInvoiceLinkedMembers,
    },
    {
      label: `${t('invoice:eInvoice.tabLinkedPrefix')} ${otherLabelPlural.toLowerCase()}`,
      route: RoutesEnum.EInvoiceLinkedOthers,
      rawLabel: true,
    },
    {
      label: 'invoice:eInvoice.tabApplications',
      route: RoutesEnum.EInvoiceApplications,
      badgeCount: hasPendingApplications,
    },
  ];

  return (
    <GridWrapper>
      <Grid item xs>
        <MainContentHeader title={t('invoice:eInvoicePage.pageTitle')} />
      </Grid>
      {hasAnySender === false && (
        <Grid item xs={12}>
          <Alert severity='info'>
            <AlertTitle>{t('invoice:eInvoice.noSenderWarningTitle')}</AlertTitle>
            {t('invoice:eInvoice.noSenderWarningText')}
          </Alert>
        </Grid>
      )}
      {hasAnySender !== false && (
        <Grid item xs={12}>
          <Paper>
            <PageTabNavigation pageTabs={pageTabs} />
            <Outlet context={{ refetchApplicationsBadge }} />
          </Paper>
        </Grid>
      )}
    </GridWrapper>
  );
};
